import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import {
  GHADiscountScopeRuleBody,
  ListGHAScopedRulesFilters,
  archiveGHADiscountScopeRule,
  createGHADiscountScopeRule,
  listGHADiscountScopeRules,
  reorderGHADiscountScopeRules,
  replaceGHADiscountScopeRule,
} from '~/services/ConnectionGoogleService';

import { invalidateGHADiscountSummary } from './useGHADiscountSummary';

export const ghaScopeRuleQueryKeys = {
  all: ['ghaDiscountScopeRules'] as const,
  lists: () => [...ghaScopeRuleQueryKeys.all, 'list'] as const,
  list: (filters: ListGHAScopedRulesFilters) => [...ghaScopeRuleQueryKeys.lists(), filters] as const,
};

export function useGHADiscountScopeRules(filters: ListGHAScopedRulesFilters = {}) {
  return useQuery({
    queryKey: ghaScopeRuleQueryKeys.list(filters),
    queryFn: async ({ signal }) => {
      const response = await listGHADiscountScopeRules(filters, { signal });
      return { result: response.result, total: response.total };
    },
    // Filter/toggle changes keep the previous rows visible while refetching (same pattern as
    // Pull with Hints / property management) instead of flashing the whole panel to a spinner.
    placeholderData: keepPreviousData,
  });
}

// Create/replace errors are NOT snackbarred: the registration-validation 400 ("register and
// upload to Hotel Center first") renders inline on the dialog, where the offending fields are.
export function useCreateGHADiscountScopeRule() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async (input: GHADiscountScopeRuleBody) => await createGHADiscountScopeRule(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ghaScopeRuleQueryKeys.lists() });
      invalidateGHADiscountSummary(queryClient);
      enqueueSnackbar('Scope rule created', { variant: 'success' });
    },
  });
}

export function useReplaceGHADiscountScopeRule() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: GHADiscountScopeRuleBody }) =>
      await replaceGHADiscountScopeRule(id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ghaScopeRuleQueryKeys.lists() });
      invalidateGHADiscountSummary(queryClient);
      enqueueSnackbar('Scope rule replaced', { variant: 'success' });
    },
  });
}

export function useArchiveGHADiscountScopeRule() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async (id: string) => await archiveGHADiscountScopeRule(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ghaScopeRuleQueryKeys.lists() });
      invalidateGHADiscountSummary(queryClient);
      enqueueSnackbar('Scope rule archived', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Failed to archive: ${error.message}`, { variant: 'error' });
    },
  });
}

export function useReorderGHADiscountScopeRules() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: reorderGHADiscountScopeRules,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ghaScopeRuleQueryKeys.lists() });
      invalidateGHADiscountSummary(queryClient);
    },
    // A concurrent admin may have reordered underneath us — refetch and let them replay.
    onError: (error: Error) => {
      queryClient.invalidateQueries({ queryKey: ghaScopeRuleQueryKeys.lists() });
      invalidateGHADiscountSummary(queryClient);
      enqueueSnackbar(`Reorder failed, priorities refreshed: ${error.message}`, { variant: 'error' });
    },
  });
}
